import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Text } from 'src/@vymo/ui/atoms';
import RichTextEditor from 'src/@vymo/ui/molecules/richTextEditor';
import { ReactComponent as LockIcon } from 'src/assets/icons/lock-line.svg';
import { ReactComponent as SendIcon } from 'src/assets/icons/send-plane.svg';
import { ReactComponent as UnlockIcon } from 'src/assets/icons/unlock-line.svg';
import { getCurrentUser } from 'src/models/lmsConfig/queries';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  NOTE_EDITOR_MAX_LENGTH,
  NOTE_EDITOR_TOOLBAR_CONFIG,
  NOTE_MENTION_TRIGGER,
  PRIVATE_NOTE_DESCRIPTION,
} from '../constants';
import { getCreateNotePayload, getMentions, trimHtml } from '../queries';
import { getIsParentEditorActive, getUsers } from '../selectors';
import { setIsParentEditorActive } from '../slice';
import { createNote } from '../thunk';
import { MessageEditorProps, NoteUser } from '../types';
import styles from './index.module.scss';

function MessageEditor({
  onCancel,
  parentEditor,
  parentKey,
  voId,
  isPrivate,
}: MessageEditorProps) {
  const [editorMessage, setEditorMessage] = useState('');
  const isParentEditorActive = useAppSelector(getIsParentEditorActive);
  const dispatch = useAppDispatch();
  const userCode = getCurrentUser()?.code;

  const [activeTriggerChar, setActiveTriggerChar] = useState<string | null>(
    null,
  );
  const [html, setHtml] = useState('');
  const [mention, setMentions] = useState<NoteUser[]>([]);
  const [isPrivateNote, setIsPrivateNote] = useState(Boolean(isPrivate));
  const isSendDisabled = trimHtml(html);

  useEffect(() => {
    if (isPrivateNote) {
      setEditorMessage(PRIVATE_NOTE_DESCRIPTION);
    } else {
      setEditorMessage('');
    }
  }, [isPrivateNote]);

  const userOptions = useAppSelector(getUsers);

  const selectOptionsMap = useMemo(
    () => ({
      [NOTE_MENTION_TRIGGER]: userOptions,
    }),
    [userOptions],
  );

  const sendNote = useCallback(() => {
    if (html) {
      dispatch(
        createNote(
          getCreateNotePayload({
            html,
            mention,
            userCode,
            isPrivate: isPrivateNote,
            voId,
            parentKey,
          }),
        ),
      );
      setHtml('');
    }
  }, [dispatch, html, isPrivateNote, mention, parentKey, userCode, voId]);

  const customToolbarActions = useCallback(
    () => (
      <div className={styles.editor__toolbar}>
        {!parentKey ? (
          <Button
            aria-label={
              isPrivateNote ? 'Make note public' : 'Make note private'
            }
            data-test-id="notes-editor-lock-button"
            iconProps={{
              icon: isPrivateNote ? <LockIcon /> : <UnlockIcon />,
              iconPosition: 'left',
            }}
            isIcon
            onClick={() => setIsPrivateNote((currentValue) => !currentValue)}
            onMouseDown={(event) => event.preventDefault()}
            size="small"
            type="text"
            disabled={isPrivate}
          />
        ) : (
          <span />
        )}
        <div className={styles.editor__toolbar__submit}>
          {onCancel && (
            <Button
              onClick={onCancel}
              onMouseDown={(event) => event.preventDefault()}
              size="small"
              type="text"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={() => sendNote()}
            isIcon
            iconProps={{ icon: <SendIcon />, iconPosition: 'left' }}
            type="text"
            disabled={isSendDisabled}
          />
        </div>
      </div>
    ),
    [isPrivate, isPrivateNote, isSendDisabled, onCancel, parentKey, sendNote],
  );

  if (!isParentEditorActive && parentEditor) {
    return (
      <Input
        onClick={() => dispatch(setIsParentEditorActive(true))}
        placeholder="Add Note"
      />
    );
  }
  return (
    <RichTextEditor
      richTextHtml={html}
      maxLength={NOTE_EDITOR_MAX_LENGTH}
      showToolbar
      description={
        editorMessage ? (
          <Text type="sublabel" classNames={styles.editor__description}>
            {editorMessage}
          </Text>
        ) : null
      }
      selectOptionsMap={
        isPrivateNote ? { [NOTE_MENTION_TRIGGER]: [] } : selectOptionsMap
      }
      mappedTriggerOptions={isPrivateNote ? [] : userOptions}
      allTriggerOptions={isPrivateNote ? [] : userOptions}
      setActiveTriggerChar={setActiveTriggerChar}
      activeTriggerChar={activeTriggerChar}
      onChange={(_value, _event, meta) => {
        setMentions(getMentions(meta.data?.optionsSelected));
        setHtml(meta.data.richTextHtml);
      }}
      toolbarConfig={NOTE_EDITOR_TOOLBAR_CONFIG}
      customToolbarActions={customToolbarActions}
    />
  );
}

export default MessageEditor;
